/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { type FC, forwardRef, type JSX, useEffect, useState } from 'react';
import type { TransportData } from '@/domainLogic/index.ts';
import type { Dates, DateType } from '@/app/components/search/utils';
import { useCommonClient } from '@/app/common/commonClientProvider';
import type { ClickParams } from '@/app/components/search/Curiosity/Analytics/utils';
import { DatePicker } from './DatePicker';

export enum InputType {
  CALENDAR = 'calendar',
  MONTH = 'month',
}

export type DateProps = {
  type: DateType;
  startDate?: string;
  endDate?: string;
  min?: number;
  max?: number;
  months?: Array<string[]>;
};

export interface DatePickersProps {
  // className: string;
  // onUpdate: Function;
  // innerRef: any;
  name: string;
  dates?: Dates;
  setDates: React.Dispatch<React.SetStateAction<Dates | undefined>>;
  selectedInput: number;
  setSelectedInput: React.Dispatch<React.SetStateAction<number>>;
  // handleClose: () => void;
  hasAvailability: boolean;
  hasDates: boolean;
  eventCallback: (payload: ClickParams) => void;
  showFlexButtons?: boolean;
  disableDatesOutsideBookingWindow?: boolean;
  transportData?: TransportData[];
  focusOnActiveDates?: boolean;
  getCalendarHeight?: (fixedHeadersHeight: number) => string;
  returnDateWasPreselected?: boolean;
  setReturnDateWasPreselected?: React.Dispatch<React.SetStateAction<boolean>>;
}

const scrollIntoView = (selector: string) => {
  const element = document.querySelector(selector);
  if (element) element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
};

export const DatePickers: FC<DatePickersProps> = ({
  name,
  dates,
  setDates,
  selectedInput,
  setSelectedInput,
  hasDates,
  eventCallback,
  disableDatesOutsideBookingWindow = false,
  transportData,
  focusOnActiveDates = false,
  getCalendarHeight,
  returnDateWasPreselected,
  setReturnDateWasPreselected,
}): JSX.Element => {
  const { isMobile } = useCommonClient();
  const startMonth =
    dates?.departureDate && dates.departureDate.date && !isMobile
      ? new Date(dates.departureDate.date).getMonth()
      : new Date().getMonth();
  const startYear =
    dates?.departureDate && dates.departureDate.date && !isMobile
      ? new Date(dates.departureDate.date).getFullYear()
      : new Date().getFullYear();
  const [month, setMonth] = useState<number>(startMonth);
  const [year, setYear] = useState<number>(startYear);

  useEffect(() => {
    if (focusOnActiveDates) {
      scrollIntoView('.day-active-start');
      setTimeout(() => {
        scrollIntoView('.custom-date-picker-inner-header-mobile');
      }, 100);
    }
  }, []);

  const datePickerProps = {
    month,
    year,
    dates,
    name,
    isMobile,
    selectedInput,
    hasDates,
    setMonth,
    setYear,
    setDates,
    setSelectedInput,
    eventCallback,
    disableDatesOutsideBookingWindow,
    transportData,
    getCalendarHeight,
    returnDateWasPreselected,
    setReturnDateWasPreselected,
  };

  return <DatePicker {...datePickerProps} />;
};

DatePickers.displayName = 'DatePickers';

const ForwardedDatePickers = forwardRef<DatePickersProps, any>((props, ref) => (
  // @ts-ignore
  <DatePickers {...{ innerRef: ref, ...props }} />
));

ForwardedDatePickers.displayName = 'ForwardedDatePickers';
